/**
 * House of Fuel — Toolkit Rail
 * Unified right-edge slide-out panel: Performance Advisor + Garage
 *
 * Usage (in Shopify theme.liquid before </body>):
 *   <script src="https://hof-proxy.vercel.app/widget.js" defer></script>
 */
(function () {
  'use strict';

  const PROXY_URL  = 'https://hof-proxy.vercel.app/api/chat';
  const STORE_URL  = 'https://houseoffuel.us';
  const APP_STORE_URL = 'https://apps.apple.com/us/app/house-of-fuel/id6761641912';
  const HOF_KEY    = 'hof_vehicle'; // localStorage key (widget's native shape)
  const GARAGE_KEY = 'hof_garage';  // mirrored key for drawer integration
  const APP_LIVE   = true;          // flip to true when App Store listing is live
  const DARK       = '#0C0C0C';   // app SLATE (was #171717)
  const SLATE_HI   = '#161616';   // nested slate (assistant bubbles)
  const LIGHT      = '#F1F1EF';
  const SLATE_TEXT = '#F4F4F2';   // primary light ink on slate
  const SLATE_DIM  = 'rgba(244,244,242,0.62)';
  const SLATE_FAINT= 'rgba(244,244,242,0.40)';
  const GOLD       = '#B8923D';
  const MID        = '#8A8A8A';
  const BORDER     = '#E2E2E0';

  // ── Vehicle database (Make → { Model → [{ v: Trim, h: handle }] }) ──────────
  // Single-trim models have one entry — Trim dropdown is hidden, auto-selected.
  // Trims sorted alphabetically; models sorted numerically/alphabetically.
  const VEHICLES = {
    'Aston Martin': {
      'DB11':    [{ v: 'V8/V12 (2017–present)',           h: 'aston-martin-db11-v8-v12-2017-present' }],
      'DBS':     [{ v: 'V12 (2018–2024)',                 h: 'aston-martin-dbs' }],
      'Vantage': [{ v: 'AM6 (2018–present)',              h: 'aston-martin-vantage-am6-2018-present' }],
    },
    'Audi': {
      'R8': [
        { v: 'Type 42 (2006–2015)',  h: 'audi-r8-typ42-2006-2015' },
        { v: 'Type 4S (2017+)',      h: 'audi-r8-type-4s-2017' },
      ],
      'RS3': [
        { v: '8V (2015–2020)',       h: 'audi-rs3-8v-2015-2020' },
        { v: '8Y (2022–present)',    h: 'audi-rs3-8y-2022-present' },
      ],
      'RS4': [
        { v: 'B8 (2012–2015)',       h: 'audi-rs4-b8-2012-2015' },
        { v: 'B9 (2018–present)',    h: 'audi-rs4-b9-2018-present' },
      ],
      'RS5': [
        { v: 'B8 (2013–2017)',       h: 'audi-rs5-b8-2013-2017' },
        { v: 'B9 (2018–present)',    h: 'audi-rs5-b9-2018-present' },
      ],
      'RS6': [
        { v: 'C7 (2013–2018)',       h: 'audi-rs6-c7-2013-2018' },
        { v: 'C8 (2020–present)',    h: 'audi-rs6-c8-2020-present' },
      ],
      'RS7': [
        { v: 'C7 (2013–2018)',       h: 'audi-rs7-c7-2013-2018' },
        { v: 'C8 (2020–present)',    h: 'audi-rs7-c8-2020-present' },
      ],
      'TT RS':   [{ v: '8S (2017–2023)',               h: 'audi-ttrs-8s-2017-2023' }],
    },
    'BMW M': {
      'M2': [
        { v: 'F87 (2016–2021)',      h: 'm2-f87-2016-2021' },
        { v: 'G87 (2023–present)',   h: 'm2-g87-2023-present' },
      ],
      'M3': [
        { v: 'F80 (2015–2018)',      h: 'm3-f80-2015-2018' },
        { v: 'G80 (2021–present)',   h: 'm3-g80-2021-present' },
      ],
      'M4': [
        { v: 'F82/F83 (2014–2020)', h: 'm4-f82-2014-2020' },
        { v: 'G82/G83 (2021+)',     h: 'm4-g82-2021-present' },
      ],
      'M5': [
        { v: 'F90 (2018–2023)',      h: 'bmw-m5-f90-2018-2023' },
        { v: 'G90 (2024+)',          h: 'bmw-m5-g90-2024' },
      ],
      'M6':      [{ v: 'F12/F13 (2013–2019)',          h: 'bmw-m6-f12-2013-2019' }],
      'M8':      [{ v: 'F91/F92/F93 (2020–present)',   h: 'm8-f91-f92-f93-2020-present' }],
      'Z4 M40i': [{ v: 'G29 (2019–present)',           h: 'z4-m40i-g29-2019-present' }],
    },
    'Ferrari': {
      '296 GTB/GTS':   [{ v: '2021–present',              h: 'ferrari-296-gtb-1' }],
      '360 Modena':    [{ v: '1999–2005',                  h: 'ferrari-360-modena-1999-2005' }],
      '458': [
        { v: 'Italia',               h: 'ferrari-458-italia-1' },
        { v: 'Speciale',             h: 'ferrari-458-speciale-1' },
        { v: 'Spider',               h: 'ferrari-458-spider' },
      ],
      '488': [
        { v: 'GTB',                  h: 'ferrari-488-gtb-1' },
        { v: 'Pista',                h: 'ferrari-488-pista-1' },
        { v: 'Spider',               h: 'ferrari-488-spider-1' },
      ],
      '812':           [{ v: 'Superfast/GTS (2017–2022)', h: 'ferrari-812' }],
      'F8':            [{ v: 'Tributo/Spider (2020+)',     h: 'ferrari-f8-tributo-2020-present' }],
      'F12':           [{ v: 'Berlinetta (2013–2017)',     h: 'ferrari-f12-berlinetta-2013-2017' }],
      'F430':          [{ v: '2005–2009',                  h: 'ferrari-f430-2005-2009' }],
      'Roma':          [{ v: '2020–present',               h: 'ferrari-roma-2020-present' }],
      'SF90':          [{ v: 'Stradale/Spider (2020+)',    h: 'ferrari-sf90-2020-present' }],
    },
    'Lamborghini': {
      'Aventador': [
        { v: 'LP700 (2011–2016)',    h: 'lamborghini-aventador-lp700-2011-2016' },
        { v: 'SVJ (2018–2021)',      h: 'lamborghini-aventador-svj-2018-2021' },
      ],
      'EVO':       [{ v: '2019–2024',                    h: 'lamborghini-evo-2019-2024' }],
      'Gallardo':  [{ v: 'LP560-4/LP570-4 (2008–2013)', h: 'lamborghini-gallaro-lp560-4-lp570-4-2008-2013' }],
      'Huracán': [
        { v: 'Base (2014–present)', h: 'huracan-2024-present' },
        { v: 'Performante (2017–2019)', h: 'huracan-2014-present-techina' },
        { v: 'STO (2021–2024)',     h: 'huracan-2014-present-sto' },
      ],
      'Murciélago': [{ v: '2001–2010',                  h: 'lamborghini-murcielago-2001-2010' }],
      'Revuelto':   [{ v: '2024–present',               h: 'lamborghini-revuelto-2024-present' }],
    },
    'Maserati': {
      'GranTurismo': [{ v: 'All',                        h: 'maserati-granturismo' }],
      'MC20':        [{ v: 'All',                        h: 'maserati-mc20-1' }],
    },
    'McLaren': {
      '570S':  [{ v: '2015–2021',    h: 'mclaren-570s-2015-2021' }],
      '600LT': [{ v: '2015–2021',    h: 'mclaren-600lt-2015-2021' }],
      '650S':  [{ v: '2014–2017',    h: 'mclaren-650s-2014-2017' }],
      '675LT': [{ v: '2014–2017',    h: 'mclaren-675lt-2014-2017' }],
      '720S':  [{ v: '2017–2023',    h: 'mclaren-720s-2017-2023' }],
      '750S':  [{ v: '2024+',        h: 'mclaren-750s-2024' }],
      '765LT': [{ v: '2017–2023',    h: 'mclaren-765lt-2017-2023' }],
      'Artura':[{ v: '2023–present', h: 'mclaren-artura-2023-present' }],
      'P1':    [{ v: '2013–2015',    h: 'mclaren-p1-2013-2015' }],
      'Senna': [{ v: '2018–2020',    h: 'mclaren-senna-2018-2020' }],
    },
    'Mercedes AMG': {
      'AMG GT': [{ v: 'GT/GTS/GTR/Black Series (2015–2021)', h: 'amg-gt-gts-gtr-gtr-pro-black-series-2015-2021' }],
      'C63': [
        { v: 'W204 (2008–2015)',     h: 'c63-amg-w204-2008-2015' },
        { v: 'W205 (2015–2021)',     h: 'c63-amg-w205-2015-2021' },
        { v: 'W206 (2023–present)', h: 'c63-amg-c63-w206-2023-present' },
      ],
      'E63': [
        { v: 'W212 (2010–2016)',     h: 'e63-amg-w212-2010-2016' },
        { v: 'W213 (2017–2023)',     h: 'e63-amg-w213-2017-2023' },
      ],
    },
    'Porsche': {
      '718 Cayman': [
        { v: 'Base (2016+)',         h: '718-cayman-spyder-2016' },
        { v: 'GT4 RS',               h: '718-cayman-gt4rs' },
        { v: 'GT4/Spyder',           h: '718-cayman-gt4' },
        { v: 'GTS',                  h: '718-cayman-gts' },
        { v: 'GTS 4.0',              h: '718-cayman-gts-4-0' },
        { v: 'S',                    h: '718-cayman-s' },
      ],
      '911 · 991.1': [
        { v: 'Carrera',              h: '991-1-carrera' },
        { v: 'Carrera 4',            h: '991-1-carrera-4' },
        { v: 'Carrera 4S',           h: '991-1-carrera-4s' },
        { v: 'Carrera S',            h: '991-1-carrera-s' },
        { v: 'GT2 RS',               h: '991-1-gt2rs' },
        { v: 'GT3',                  h: '991-1-gt3' },
        { v: 'GT3 RS',               h: '991-1-gt3rs' },
        { v: 'GTS/4GTS',             h: '991-1-gts-4gts' },
        { v: 'Turbo',                h: '991-1-turbo' },
        { v: 'Turbo S',              h: '991-1-turbo-s' },
      ],
      '911 · 991.2': [
        { v: 'Carrera',              h: '991-2-carrera' },
        { v: 'Carrera 4',            h: '991-2-carrera-4' },
        { v: 'Carrera 4S',           h: '991-2-carrera-4s' },
        { v: 'Carrera GTS',          h: '991-2-carrera-gts' },
        { v: 'Carrera S',            h: '991-2-carrera-s' },
        { v: 'GT2 RS',               h: '991-2-gt2rs' },
        { v: 'GT3',                  h: '991-2-gt3' },
        { v: 'GT3 RS',               h: '991-2-gt3rs' },
        { v: 'GTS/4GTS',             h: '991-2-gts-4gts' },
        { v: 'Turbo',                h: '991-2-turbo' },
        { v: 'Turbo S',              h: '991-2-turbo-s' },
      ],
      '911 · 992.1': [
        { v: 'Carrera',              h: '992-1-carrera' },
        { v: 'Carrera 4',            h: '992-1-carrera-4' },
        { v: 'Carrera 4S',           h: '992-1-carrera-4s' },
        { v: 'Carrera S',            h: '992-1-carrera-s' },
        { v: 'GT3',                  h: '992-1-gt3' },
        { v: 'GT3 RS',               h: '992-1-gt3rs' },
        { v: 'GT3 Touring',          h: '992-1-gt3-touring' },
        { v: 'Turbo',                h: '992-1-turbo' },
        { v: 'Turbo S',              h: '992-1-turbo-s' },
      ],
      '911 · 992.2': [
        { v: 'Carrera',              h: '992-2-carrera' },
        { v: 'Carrera 4',            h: '992-2-carrera-4' },
        { v: 'Carrera 4S',           h: '992-2-carrera-4s' },
        { v: 'Carrera S',            h: '992-2-carrera-s' },
        { v: 'GT3',                  h: '992-2-gt3' },
        { v: 'GT3 Touring',          h: '992-2-gt3-touring' },
        { v: 'GTS',                  h: '992-2-gts' },
      ],
      '911 · 997.1': [
        { v: 'Carrera',              h: '997-1-carrera' },
        { v: 'Carrera 4',            h: '997-1-carrera-4' },
        { v: 'Carrera 4S',           h: '997-1-carrera-4s' },
        { v: 'Carrera S',            h: '997-1-carrera-s' },
        { v: 'GT3',                  h: '997-1-gt3' },
        { v: 'GTS/4GTS',             h: '997-1-gts-4gts' },
        { v: 'Turbo',                h: '997-1-turbo' },
      ],
      '911 · 997.2': [
        { v: 'Carrera',              h: '997-2-carrera' },
        { v: 'Carrera 4',            h: '997-2-carrera-4' },
        { v: 'Carrera 4S',           h: '997-2-carrera-4s' },
        { v: 'Carrera S',            h: '997-2-carrera-s' },
        { v: 'GT2 RS',               h: '997-2-gt2rs' },
        { v: 'GT3',                  h: '997-2-gt3' },
        { v: 'GT3 RS',               h: '997-2-gt3-rs' },
        { v: 'GTS/4GTS',             h: '997-2-gts-4gts' },
        { v: 'Turbo',                h: '997-2-turbo' },
        { v: 'Turbo S',              h: '997-2-turbo-s' },
      ],
      '981 Cayman': [
        { v: 'Base (2013–2016)',     h: '981-cayman-spyder-2013-2016' },
        { v: 'GT4/Spyder',           h: '981-cayman-gt4' },
        { v: 'GTS',                  h: '981-cayman-gts' },
        { v: 'S',                    h: '981-cayman-s' },
      ],
    },
  };
  const RED       = '#C0392B';
  const GREEN     = '#4ade80';

  // ── Detect Shopify product context ──────────────────────────────────────────
  function getPageContext() {
    try {
      const meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (meta && meta.product) {
        return `Customer is viewing: ${meta.product.title} (${meta.product.type || 'product'})`;
      }
    } catch (e) {}
    const path = window.location.pathname;
    if (path.includes('/products/')) {
      const slug = path.split('/products/')[1]?.split('?')[0]?.replace(/-/g, ' ');
      if (slug) return `Customer is viewing product: ${slug}`;
    }
    if (path.includes('/collections/')) {
      const slug = path.split('/collections/')[1]?.split('?')[0]?.replace(/-/g, ' ');
      if (slug) return `Customer is browsing collection: ${slug}`;
    }
    return null;
  }

  // ── Styles ───────────────────────────────────────────────────────────────────
  const CSS = `

    /* ── Hide original Shopify GARAGE tab ───────────────────────────── */
    [class*="garage-sidebar"], [id*="garage-sidebar"],
    .shopify-section--garage, #shopify-section-garage,
    .garage-tab, #garage-tab { display: none !important; }

    /* ── Hide EasySearch native section ─────────────────────────────── */
    [id*="easysearch"], [id*="easy-search"], [id*="EasySearch"],
    [class*="easysearch"], [class*="easy-search"],
    [data-section-type*="easy-search"] { display: none !important; }

    /* ── HOF Search by Vehicle section ──────────────────────────────── */
    /* Styles moved to the hof-garage.liquid theme section file.         */
    /* Widget hydrates the server-rendered #hof-search-section only.     */

    /* ── HOF Product Fitment Block ───────────────────────────────────── */
    /* Styles moved to the vehicle_fitment.liquid theme block file.     */
    /* Widget hydrates the server-rendered #hof-fitment-block only.     */

    /* ── Rail tab (collapsed trigger) ──────────────────────────────── */
    #hof-rail {
      position: fixed;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      z-index: 99998;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      background: ${DARK};
      border-radius: 12px 0 0 12px;
      padding: 12px 8px;
      cursor: pointer;
      box-shadow: -4px 0 24px rgba(0,0,0,0.25);
      transition: padding 0.2s ease, background 0.2s ease;
      animation: hofRailIn 0.5s cubic-bezier(0.34,1.56,0.64,1) both;
    }
    #hof-rail:hover {
      background: #222;
      padding-right: 10px;
    }
    @keyframes hofRailIn {
      from { opacity: 0; transform: translateY(-50%) translateX(20px); }
      to   { opacity: 1; transform: translateY(-50%) translateX(0); }
    }

    /* Rail icon buttons */
    .hof-rail-icon {
      width: 36px; height: 36px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 8px;
      transition: background 0.15s;
      position: relative;
    }
    .hof-rail-icon:hover { background: rgba(255,255,255,0.1); }
    .hof-rail-icon svg   { display: block; }
    .hof-rail-divider {
      width: 20px; height: 1px;
      background: rgba(255,255,255,0.1);
      margin: 4px 0;
    }

    /* Online dot on advisor icon */
    #hof-rail-dot {
      position: absolute;
      top: 6px; right: 6px;
      width: 6px; height: 6px;
      border-radius: 50%;
      background: ${GREEN};
      box-shadow: 0 0 5px rgba(74,222,128,0.8);
      animation: hofDotPulse 2.5s ease-in-out infinite;
    }
    @keyframes hofDotPulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50%       { opacity: 0.6; transform: scale(0.8); }
    }

    /* ── Slide-out panel ────────────────────────────────────────────── */
    #hof-panel {
      position: fixed;
      top: 14px; right: 14px; bottom: 14px;
      width: 420px;
      max-width: calc(100vw - 28px);
      background: ${LIGHT};
      z-index: 99999;
      display: flex;
      flex-direction: column;
      box-shadow: 0 18px 60px rgba(0,0,0,0.28);
      transform: translateX(calc(100% + 28px));
      transition: transform 0.34s cubic-bezier(0.32,0.72,0,1);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      border-radius: 22px;
      overflow: hidden;
    }
    #hof-panel.hof-open {
      transform: translateX(0);
    }

    /* ── Panel top nav ──────────────────────────────────────────────── */
    #hof-panel-nav {
      background: ${DARK};
      display: flex;
      align-items: center;
      padding: 0 12px;
      flex-shrink: 0;
      gap: 4px;
      height: 56px;
    }
    #hof-panel-logo {
      width: 28px; height: 28px;
      display: flex; align-items: center; justify-content: center;
      margin-right: 8px;
    }
    .hof-nav-tab {
      flex: 1; height: 100%;
      display: flex; align-items: center; justify-content: center; gap: 7px;
      background: none; border: none; cursor: pointer;
      color: rgba(241,241,239,0.45);
      font-size: 13px; font-weight: 600; letter-spacing: 0.02em;
      border-bottom: 2px solid transparent;
      transition: color 0.15s, border-color 0.15s;
      font-family: inherit;
      padding: 0 4px;
    }
    .hof-nav-tab:hover { color: rgba(241,241,239,0.75); }
    .hof-nav-tab.active {
      color: ${LIGHT};
      border-bottom-color: ${LIGHT};
    }
    .hof-nav-tab svg { flex-shrink: 0; }
    #hof-panel-close {
      width: 32px; height: 32px;
      background: rgba(255,255,255,0.08); border: none; cursor: pointer;
      border-radius: 8px;
      display: flex; align-items: center; justify-content: center;
      color: rgba(241,241,239,0.55);
      transition: background 0.15s;
      margin-left: 4px;
      flex-shrink: 0;
    }
    #hof-panel-close:hover { background: rgba(255,255,255,0.15); }

    /* ── Tab content areas ──────────────────────────────────────────── */
    .hof-tab-content {
      display: none;
      flex: 1;
      flex-direction: column;
      overflow: hidden;
    }
    .hof-tab-content.active { display: flex; }

    /* ── Advisor tab (chat) ─────────────────────────────────────────── */
    #hof-tab-advisor { background: ${DARK}; }
    .hof-messages {
      flex: 1; overflow-y: auto; padding: 18px 16px 10px;
      display: flex; flex-direction: column; gap: 12px;
      scroll-behavior: smooth;
      background: ${DARK};
    }
    .hof-messages::-webkit-scrollbar { width: 5px; }
    .hof-messages::-webkit-scrollbar-track { background: transparent; }
    .hof-messages::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.16); border-radius: 3px; }
    .hof-messages::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.28); }

    .hof-welcome {
      display: flex; flex-direction: column; align-items: center;
      padding: 28px 10px 8px; text-align: center; gap: 6px;
    }
    .hof-welcome-icon {
      width: 52px; height: 52px; border-radius: 50%;
      background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.16);
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 4px;
    }
    .hof-welcome-title {
      font-size: 13px; font-weight: 700; color: ${SLATE_TEXT};
      text-transform: uppercase; letter-spacing: 0.14em;
    }
    .hof-welcome-sub   { font-size: 13px; color: ${SLATE_DIM}; line-height: 1.55; max-width: 264px; }
    .hof-suggestions   { display: flex; flex-direction: column; gap: 7px; width: 100%; margin-top: 14px; }
    .hof-suggestion {
      background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); border-radius: 12px;
      padding: 12px 15px; font-size: 13px; color: ${SLATE_TEXT}; font-weight: 500;
      cursor: pointer; text-align: left; display: flex; align-items: center;
      justify-content: space-between; gap: 10px; line-height: 1.35;
      transition: border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
      font-family: inherit;
    }
    .hof-suggestion span { color: ${SLATE_DIM}; flex-shrink: 0; transition: transform 0.18s ease, color 0.18s ease; }
    .hof-suggestion:hover { border-color: rgba(255,255,255,0.3); transform: translateY(-1px); box-shadow: 0 6px 18px -10px rgba(0,0,0,0.5); }
    .hof-suggestion:hover span { color: ${SLATE_TEXT}; transform: translateX(2px); }
    .hof-appstore-cta {
      display: flex; align-items: center; gap: 9px; width: 100%; margin-top: 14px;
      background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.14);
      border-radius: 12px; padding: 12px 15px; color: ${SLATE_TEXT};
      text-decoration: none; font-size: 13px; font-weight: 600;
      transition: border-color 0.18s ease, background 0.18s ease;
    }
    .hof-appstore-cta:hover { border-color: ${GOLD}; background: rgba(184,146,61,0.12); }
    .hof-appstore-cta > span:not(.hof-appstore-arrow) { flex: 1; }
    .hof-appstore-cta .hof-appstore-arrow { color: ${SLATE_DIM}; flex-shrink: 0; }
    .hof-appstore-cta svg { flex-shrink: 0; }

    .hof-row        { display: flex; align-items: flex-end; gap: 8px; animation: hofMsgIn 0.32s cubic-bezier(0.2,0.8,0.2,1) both; }
    .hof-row.user   { flex-direction: row-reverse; }
    @keyframes hofMsgIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
    @media (prefers-reduced-motion: reduce) { .hof-row { animation: none; } }
    .hof-avatar {
      width: 28px; height: 28px; border-radius: 50%;
      background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.16); flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
    }
    .hof-avatar.error { background: ${RED}; border-color: rgba(192,57,43,0.4); }
    .hof-bubble {
      max-width: 82%; border-radius: 18px;
      padding: 11px 15px; font-size: 14px; line-height: 1.6;
      word-wrap: break-word;
    }
    .hof-bubble.assistant {
      background: ${SLATE_HI}; color: ${SLATE_TEXT}; border: 1px solid rgba(255,255,255,0.10);
      border-bottom-left-radius: 5px;
    }
    .hof-bubble.user {
      background: rgba(255,255,255,0.10); color: ${SLATE_TEXT};
      border: 1px solid rgba(255,255,255,0.16); border-bottom-right-radius: 5px;
    }
    .hof-bubble.error { background: rgba(192,57,43,0.18); border: 1px solid rgba(192,57,43,0.4); color: #f3a59b; }
    .hof-bubble.assistant a {
      color: ${SLATE_TEXT}; text-decoration: underline; text-underline-offset: 2px;
      text-decoration-color: ${SLATE_DIM}; word-break: break-word; font-weight: 500;
    }
    .hof-bubble.assistant a:hover { text-decoration-color: ${SLATE_TEXT}; }

    .hof-typing {
      display: flex; align-items: center; gap: 4px; padding: 12px 16px;
      background: ${SLATE_HI}; border: 1px solid rgba(255,255,255,0.10); border-radius: 18px;
      border-bottom-left-radius: 5px; width: fit-content;
    }
    .hof-dot {
      width: 6px; height: 6px; border-radius: 50%;
      background: ${SLATE_DIM}; animation: hofBounce 1.2s infinite;
    }
    .hof-dot:nth-child(2) { animation-delay: 0.2s; }
    .hof-dot:nth-child(3) { animation-delay: 0.4s; }
    @keyframes hofBounce {
      0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
      30%            { transform: translateY(-4px); opacity: 1; }
    }

    .hof-input-bar {
      display: flex; align-items: flex-end; gap: 8px;
      padding: 12px;
      border-top: 1px solid rgba(255,255,255,0.10);
      background: ${DARK};
      flex-shrink: 0;
    }
    .hof-input {
      flex: 1; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.16);
      border-radius: 14px; padding: 11px 15px;
      font-size: 14px; color: ${SLATE_TEXT}; resize: none;
      outline: none; max-height: 90px; line-height: 1.45;
      font-family: inherit; transition: border-color 0.18s ease;
    }
    .hof-input:focus { border-color: rgba(255,255,255,0.34); }
    .hof-input::placeholder { color: ${SLATE_FAINT}; }
    .hof-send {
      width: 40px; height: 40px; border-radius: 50%;
      background: ${GOLD}; border: none; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0; transition: opacity 0.15s ease, transform 0.15s ease;
    }
    .hof-send:not(:disabled):hover { transform: translateY(-1px); }
    .hof-send:disabled { opacity: 0.28; cursor: default; }

    .hof-list-item  { display: flex; align-items: flex-start; gap: 8px; margin: 4px 0; }
    .hof-list-num   { background: ${SLATE_TEXT}; color: ${DARK}; font-size: 11px; font-weight: 700; min-width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
    .hof-list-text  { flex: 1; line-height: 1.5; }
    .hof-step-label { display: inline-block; font-weight: 600; color: ${SLATE_TEXT}; margin-right: 4px; }
    .hof-product-link { display: inline-flex; align-items: center; gap: 4px; background: ${SLATE_TEXT}; color: ${DARK} !important; text-decoration: none !important; font-size: 12px; font-weight: 700; letter-spacing: 0.02em; padding: 6px 13px; border-radius: 20px; margin: 6px 0 2px; transition: opacity 0.15s; }
    .hof-product-link:hover { opacity: 0.85; }

    .hof-chat-footer { text-align: center; padding: 6px 0 10px; font-size: 10px; color: ${SLATE_DIM}; flex-shrink: 0; background: ${DARK}; }
    .hof-chat-footer a { color: ${SLATE_DIM}; text-decoration: none; }
    .hof-chat-footer a:hover { text-decoration: underline; }

    /* ── Garage tab ─────────────────────────────────────────────────── */
    #hof-tab-garage {
      align-items: stretch;
      justify-content: flex-start;
      padding: 24px 20px;
      gap: 0;
      overflow-y: auto;
    }

    /* ── Vehicle selector ──────────────────────────────────────────── */
    .hof-vs-row { margin-bottom: 10px; }
    .hof-vs-label {
      display: block; font-size: 10px; font-weight: 700;
      letter-spacing: 0.07em; text-transform: uppercase;
      color: ${MID}; margin-bottom: 6px;
    }
    .hof-select {
      width: 100%; background: #fff;
      border: 1px solid ${BORDER}; border-radius: 10px;
      padding: 11px 36px 11px 13px;
      font-size: 14px; color: ${DARK}; font-family: inherit;
      outline: none; cursor: pointer;
      -webkit-appearance: none; appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M6 9l6 6 6-6' stroke='%238A8A8A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
      background-repeat: no-repeat; background-position: right 12px center;
      background-size: 14px; box-sizing: border-box;
    }
    .hof-select:focus { border-color: ${DARK}; }
    .hof-select:disabled { opacity: 0.4; cursor: default; background-color: #fafafa; }
    .hof-vs-btn {
      width: 100%; background: ${DARK}; color: ${LIGHT};
      border: none; border-radius: 10px; padding: 13px;
      font-size: 14px; font-weight: 600; font-family: inherit;
      cursor: pointer; margin-top: 6px; transition: opacity 0.15s;
      box-sizing: border-box;
    }
    .hof-vs-btn:hover:not(:disabled) { opacity: 0.82; }
    .hof-vs-btn:disabled { opacity: 0.35; cursor: default; }

    /* ── Saved vehicle card ─────────────────────────────────────────── */
    .hof-vehicle-card {
      background: ${DARK}; border-radius: 14px;
      padding: 15px 16px; display: flex; align-items: center;
      gap: 12px; margin-bottom: 16px; box-sizing: border-box;
    }
    .hof-vehicle-card-icon {
      width: 38px; height: 38px; border-radius: 9px;
      background: rgba(255,255,255,0.1);
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .hof-vehicle-card-info { flex: 1; min-width: 0; }
    .hof-vehicle-card-make {
      font-size: 10px; font-weight: 700; letter-spacing: 0.08em;
      color: rgba(241,241,239,0.45); text-transform: uppercase; margin-bottom: 2px;
    }
    .hof-vehicle-card-name {
      font-size: 14px; font-weight: 700; color: ${LIGHT};
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .hof-vehicle-card-change {
      font-size: 12px; color: rgba(241,241,239,0.5); background: none;
      border: 1px solid rgba(241,241,239,0.2); border-radius: 6px;
      cursor: pointer; font-family: inherit; padding: 5px 10px;
      flex-shrink: 0; transition: all 0.15s;
    }
    .hof-vehicle-card-change:hover { color: ${LIGHT}; border-color: rgba(241,241,239,0.5); }

    /* ── Multi-car garage list ──────────────────────────────────────── */
    .hof-garage-cars { display: flex; flex-direction: column; gap: 8px; }
    .hof-garage-car {
      display: flex; align-items: center; gap: 12px;
      background: #fff; border: 1px solid ${BORDER}; border-radius: 12px;
      padding: 12px 14px; cursor: pointer; box-sizing: border-box;
      transition: border-color 0.15s, background 0.15s;
    }
    .hof-garage-car:hover { background: #f7f7f5; }
    .hof-garage-car.active { background: ${DARK}; border-color: ${DARK}; }
    .hof-garage-car-dot { width: 8px; height: 8px; border-radius: 50%; border: 1.5px solid ${MID}; flex-shrink: 0; box-sizing: border-box; }
    .hof-garage-car.active .hof-garage-car-dot { background: #4ade80; border-color: #4ade80; }
    .hof-garage-car-info { flex: 1; min-width: 0; }
    .hof-garage-car-make { font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: ${MID}; margin-bottom: 1px; }
    .hof-garage-car.active .hof-garage-car-make { color: rgba(241,241,239,0.45); }
    .hof-garage-car-name { font-size: 14px; font-weight: 700; color: ${DARK}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .hof-garage-car.active .hof-garage-car-name { color: ${LIGHT}; }
    .hof-garage-car-remove {
      background: none; border: none; cursor: pointer; flex-shrink: 0;
      color: ${MID}; font-size: 17px; line-height: 1; padding: 4px 7px;
      font-family: inherit; border-radius: 6px; transition: color 0.15s;
    }
    .hof-garage-car-remove:hover { color: #b91c1c; }
    .hof-garage-car.active .hof-garage-car-remove { color: rgba(241,241,239,0.5); }
    .hof-garage-car.active .hof-garage-car-remove:hover { color: #fca5a5; }
    .hof-garage-add-btn {
      width: 100%; background: none; border: 1px dashed ${BORDER}; border-radius: 12px;
      padding: 12px; font-size: 13px; font-weight: 600; color: ${MID};
      cursor: pointer; font-family: inherit; margin-top: 10px;
      transition: all 0.15s; box-sizing: border-box;
    }
    .hof-garage-add-btn:hover { color: ${DARK}; border-color: ${DARK}; }
    .hof-garage-add-form { margin-top: 14px; }

    /* ── Advisor active-vehicle selector (full-width, dark — matches app) ── */
    #hof-advisor-vehicle { position: relative; padding: 12px 16px 4px; flex-shrink: 0; }
    .hof-veh-chip {
      display: flex; align-items: center; gap: 9px; width: 100%; box-sizing: border-box;
      background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.16);
      border-radius: 14px;
      padding: 13px 15px; font-size: 14px; font-weight: 600; color: ${SLATE_TEXT};
      cursor: pointer; font-family: inherit;
      transition: border-color 0.15s ease, background 0.15s ease;
    }
    .hof-veh-chip:hover { border-color: rgba(255,255,255,0.34); background: rgba(255,255,255,0.09); }
    .hof-veh-chip svg { width: 16px; height: 16px; flex-shrink: 0; opacity: 0.85; }
    .hof-veh-chip-label { flex: 1; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .hof-veh-chip-caret { opacity: 0.5; font-size: 9px; color: ${SLATE_DIM}; flex-shrink: 0; }
    .hof-veh-menu {
      position: absolute; top: calc(100% + 4px); left: 16px; right: 16px; z-index: 6;
      background: ${SLATE_HI}; border: 1px solid rgba(255,255,255,0.14); border-radius: 14px;
      box-shadow: 0 16px 40px rgba(0,0,0,0.5); overflow: hidden; display: none;
    }
    .hof-veh-menu.open { display: block; }
    .hof-veh-menu-item {
      display: flex; align-items: center; gap: 8px; width: 100%;
      background: none; border: none; border-bottom: 1px solid rgba(255,255,255,0.08);
      padding: 13px 15px; font-size: 13px; color: ${SLATE_TEXT}; font-family: inherit;
      cursor: pointer; text-align: left; transition: background 0.15s;
    }
    .hof-veh-menu-item:last-child { border-bottom: none; }
    .hof-veh-menu-item:hover { background: rgba(255,255,255,0.06); }
    .hof-veh-menu-item .check { margin-left: auto; font-weight: 700; color: ${GOLD}; }
    .hof-veh-menu-item.add { color: ${SLATE_DIM}; font-weight: 600; }

    /* ── Add-to-garage suggestion chip (in chat) ────────────────────── */
    .hof-add-garage-chip {
      display: inline-flex; align-items: center; gap: 6px;
      margin-top: 10px; background: ${DARK}; color: ${LIGHT};
      border: none; border-radius: 999px; padding: 8px 14px;
      font-size: 12px; font-weight: 600; font-family: inherit; cursor: pointer;
      transition: opacity 0.15s;
    }
    .hof-add-garage-chip:hover:not(:disabled) { opacity: 0.85; }
    .hof-add-garage-chip:disabled { cursor: default; }
    .hof-add-garage-chip.added { background: #052e16; color: #4ade80; }
    /* NOTE: homepage Shop-by-Vehicle garage strip styles live in the
       HOF-Garage.liquid theme section (auto light/dark, editable in the
       theme editor) — the widget only renders the markup for it. */

    /* ── Fitment banner ─────────────────────────────────────────────── */
    #hof-fitment-banner {
      position: fixed; top: 0; left: 0; right: 0;
      z-index: 99997; padding: 10px 16px;
      display: flex; align-items: center; gap: 10px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 13px; font-weight: 500;
      transform: translateY(-100%);
      transition: transform 0.3s cubic-bezier(0.32,0.72,0,1);
    }
    #hof-fitment-banner.hof-banner-visible { transform: translateY(0); }
    #hof-fitment-banner.hof-fits {
      background: #052e16; color: #4ade80;
      border-bottom: 1px solid rgba(74,222,128,0.2);
    }
    #hof-fitment-banner.hof-no-fit {
      background: #2d0a0a; color: #fca5a5;
      border-bottom: 1px solid rgba(248,113,113,0.2);
    }
    .hof-banner-icon { flex-shrink: 0; }
    .hof-banner-text { flex: 1; }
    .hof-banner-text strong { font-weight: 700; }
    .hof-banner-cta {
      font-size: 12px; font-weight: 600; color: inherit;
      text-decoration: underline; opacity: 0.8; white-space: nowrap;
    }
    .hof-banner-cta:hover { opacity: 1; }
    .hof-banner-x {
      background: none; border: none; cursor: pointer;
      color: inherit; opacity: 0.5; font-size: 18px; line-height: 1;
      padding: 2px 6px; font-family: inherit; flex-shrink: 0;
    }
    .hof-banner-x:hover { opacity: 1; }

    /* ── Rail garage indicator dot ──────────────────────────────────── */
    #hof-rail-garage-dot {
      position: absolute; top: 6px; right: 6px;
      width: 6px; height: 6px; border-radius: 50%;
      background: ${LIGHT}; display: none;
    }
    .hof-garage-hero {
      width: 100%; background: ${DARK}; border-radius: 16px;
      padding: 28px 24px; display: flex; flex-direction: column;
      align-items: center; text-align: center; gap: 10px;
      margin-bottom: 20px;
    }
    .hof-garage-hero-icon {
      width: 52px; height: 52px; border-radius: 50%;
      background: rgba(255,255,255,0.1);
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 4px;
    }
    .hof-garage-hero-title { color: ${LIGHT}; font-size: 17px; font-weight: 700; }
    .hof-garage-hero-sub   { color: rgba(241,241,239,0.55); font-size: 13px; line-height: 1.5; max-width: 240px; }
    .hof-app-btns { display: flex; flex-direction: column; gap: 8px; width: 100%; margin-top: 8px; }
    .hof-app-btn {
      display: flex; align-items: center; justify-content: center; gap: 8px;
      background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.15);
      border-radius: 10px; padding: 11px 16px; color: ${LIGHT};
      font-size: 13px; font-weight: 600; text-decoration: none;
      transition: background 0.15s; font-family: inherit;
    }
    .hof-app-btn:hover { background: rgba(255,255,255,0.18); }

    .hof-garage-links { width: 100%; display: flex; flex-direction: column; gap: 8px; }
    .hof-garage-link {
      display: flex; align-items: center; justify-content: space-between;
      background: #fff; border: 1px solid ${BORDER}; border-radius: 12px;
      padding: 14px 16px; color: ${DARK}; text-decoration: none;
      font-size: 14px; font-weight: 500;
      transition: background 0.15s; font-family: inherit;
    }
    .hof-garage-link:hover { background: #f7f7f5; }
    .hof-garage-link-left  { display: flex; align-items: center; gap: 12px; }
    .hof-garage-link-icon  { width: 36px; height: 36px; border-radius: 8px; background: ${LIGHT}; display: flex; align-items: center; justify-content: center; }

    /* ── Overlay (mobile backdrop) ──────────────────────────────────── */
    #hof-overlay {
      display: none;
      position: fixed; inset: 0;
      background: rgba(0,0,0,0.4);
      z-index: 99998;
      backdrop-filter: blur(2px);
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    #hof-overlay.hof-visible { opacity: 1; }

    /* ── Mobile ─────────────────────────────────────────────────────── */
    @media (max-width: 600px) {
      #hof-overlay { display: block; pointer-events: none; }
      #hof-overlay.hof-visible { pointer-events: all; }
      #hof-panel {
        top: 0; right: 0; bottom: 0; left: 0;
        width: 100vw; max-width: 100vw;
        border-radius: 0;
        transform: translateX(100%);
      }
      #hof-rail { top: auto; bottom: 100px; transform: none; }
      @keyframes hofRailIn {
        from { opacity: 0; transform: translateX(20px); }
        to   { opacity: 1; transform: translateX(0); }
      }
    }

    @media (min-width: 1200px) {
      #hof-panel { width: 460px; }
    }
  `;

  const SUGGESTIONS_LIST = [
    'What mods should I do first?',
    'Best exhaust upgrade for my car?',
    'Build me a stage 1 mod list',
    'What suspension setup for track days?',
  ];

  // ── SVG icons ─────────────────────────────────────────────────────────────
  const ICON_HF = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="${LIGHT}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  const ICON_CHAT = `<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M12 3C7.03 3 3 6.58 3 11c0 2.11.87 4.03 2.29 5.47L4 21l4.69-1.23C9.97 20.57 10.97 21 12 21c4.97 0 9-3.58 9-8s-4.03-8-9-8z" fill="currentColor"/></svg>`;
  const ICON_CAR  = `<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M5 17H3a2 2 0 01-2-2v-4l2.5-6h13L19 11v4a2 2 0 01-2 2h-2M5 17a2 2 0 104 0 2 2 0 00-4 0zm10 0a2 2 0 104 0 2 2 0 00-4 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  const ICON_CLOSE = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="${LIGHT}" stroke-width="2.5" stroke-linecap="round"/></svg>`;
  const ICON_CHEVRON = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="${LIGHT}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  const ICON_ARROW = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M12 5l7 7-7 7" stroke="${LIGHT}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  const ICON_APPLE = `<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>`;

  // ── Build DOM ─────────────────────────────────────────────────────────────
  function buildWidget() {
    const style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);

    // ── Rail tab ─────────────────────────────────────────────────────────────
    const rail = document.createElement('div');
    rail.id = 'hof-rail';
    rail.setAttribute('aria-label', 'Open HOF Toolkit');
    rail.innerHTML = `
      <div class="hof-rail-icon" id="hof-rail-advisor" title="Performance Advisor">
        <div id="hof-rail-dot"></div>
        ${ICON_CHAT.replace('currentColor', LIGHT)}
      </div>
      <div class="hof-rail-divider"></div>
      <div class="hof-rail-icon" id="hof-rail-garage" title="My Garage">
        <div id="hof-rail-garage-dot"></div>
        ${ICON_CAR.replace('currentColor', 'rgba(241,241,239,0.55)')}
      </div>
      <div class="hof-rail-divider"></div>
      <div class="hof-rail-icon" style="opacity:0.4">
        ${ICON_CHEVRON}
      </div>`;
    document.body.appendChild(rail);

    // ── Overlay ───────────────────────────────────────────────────────────────
    const overlay = document.createElement('div');
    overlay.id = 'hof-overlay';
    document.body.appendChild(overlay);

    // ── Panel ─────────────────────────────────────────────────────────────────
    const panel = document.createElement('div');
    panel.id = 'hof-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'House of Fuel Toolkit');
    panel.innerHTML = `
      <div id="hof-panel-nav">
        <div id="hof-panel-logo">${ICON_HF}</div>
        <button class="hof-nav-tab active" data-tab="advisor">
          ${ICON_CHAT.replace('currentColor', 'currentColor')}
          Advisor
        </button>
        <button class="hof-nav-tab" data-tab="garage">
          ${ICON_CAR.replace('currentColor', 'currentColor')}
          Garage
        </button>
        <button id="hof-panel-close" aria-label="Close">${ICON_CLOSE}</button>
      </div>

      <!-- Advisor tab -->
      <div class="hof-tab-content active" id="hof-tab-advisor">
        <div id="hof-advisor-vehicle"></div>
        <div class="hof-messages" id="hof-messages"></div>
        <div class="hof-input-bar">
          <textarea class="hof-input" id="hof-input" placeholder="Ask about mods, parts, builds…" rows="1"></textarea>
          <button class="hof-send" id="hof-send" disabled aria-label="Send">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12l7-7 7 7" stroke="${LIGHT}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <div class="hof-chat-footer">Powered by <a href="https://houseoffuel.us" target="_blank">House of Fuel</a></div>
      </div>

      <!-- Garage tab (rendered dynamically via renderGarageTab()) -->
      <div class="hof-tab-content" id="hof-tab-garage"></div>
    `;
    document.body.appendChild(panel);

    // ── State ─────────────────────────────────────────────────────────────────
    let isOpen      = false;
    let activeTab   = 'advisor';
    let isLoading   = false;
    let chatHistory = [];
    const pageContext = getPageContext();

    // ── Vehicle state (localStorage) ─────────────────────────────────────────
    // hof_vehicle          = the ACTIVE car (unchanged shape — every existing
    //                        consumer keeps reading this key)
    // hof_garage_cars      = the full multi-car garage list
    function loadVehicle() {
      try { return JSON.parse(localStorage.getItem(HOF_KEY)); } catch (e) { return null; }
    }
    const GARAGE_LIST_KEY = 'hof_garage_cars';
    function loadGarage() {
      try {
        const list = JSON.parse(localStorage.getItem(GARAGE_LIST_KEY));
        if (Array.isArray(list)) return list.filter((c) => c && c.handle);
      } catch (e) {}
      // Migration: pre-multi-car users had a single hof_vehicle
      const single = loadVehicle();
      const migrated = single ? [single] : [];
      if (migrated.length) saveGarageList(migrated);
      return migrated;
    }
    function saveGarageList(list) {
      try { localStorage.setItem(GARAGE_LIST_KEY, JSON.stringify(list)); } catch (e) {}
    }
    // Reverse-lookup a collection handle → {make, model, trim, handle} so cars
    // detected by the advisor can be saved into the garage.
    function vehicleFromHandle(handle) {
      for (const make of Object.keys(VEHICLES)) {
        for (const model of Object.keys(VEHICLES[make])) {
          const trims = VEHICLES[make][model];
          for (const t of trims) {
            if (t.h === handle) {
              return { make, model, trim: trims.length === 1 ? '' : t.v, handle };
            }
          }
        }
      }
      return null;
    }
    // Full display label: "911 · 997.1 Carrera S" or "360 Modena" (no trim)
    function vehicleLabel(saved) {
      if (!saved) return '';
      return saved.trim ? `${saved.model} ${saved.trim}` : saved.model;
    }
    // Mirror native widget shape (hof_vehicle) → drawer shape (hof_garage).
    // Keeps the side-by-side drawer's Systems vehicle card in sync.
    function mirrorToGarageKey(saved) {
      try {
        if (saved) {
          localStorage.setItem(GARAGE_KEY, JSON.stringify({
            make:              saved.make,
            model:             saved.model,
            label:             vehicleLabel(saved),
            collection_handle: saved.handle,
          }));
        } else {
          localStorage.removeItem(GARAGE_KEY);
        }
      } catch (e) {}
    }
    function broadcastGarageUpdate() {
      try {
        window.dispatchEvent(new CustomEvent('hof:garage-updated'));
        document.dispatchEvent(new CustomEvent('hof:garage-updated'));
      } catch (e) {}
    }
    // Re-render everything that shows the active vehicle.
    function refreshVehicleUI() {
      updateRailVehicle();
      renderGarageTab();
      renderSearchSection();
      hydrateProductFitmentBlock();
      renderAdvisorChip();
    }
    // Set the ACTIVE car — all existing consumers (drawer, fitment, systems)
    // keep reading hof_vehicle, so swapping cars updates the whole site.
    function setActiveVehicle(data) {
      if (data) localStorage.setItem(HOF_KEY, JSON.stringify(data));
      else localStorage.removeItem(HOF_KEY);
      mirrorToGarageKey(data);
      broadcastGarageUpdate();
      refreshVehicleUI();
    }
    // Add a car to the garage list (dedupe by handle); optionally make it active.
    function addToGarage(data, activate) {
      const list = loadGarage().filter((c) => c.handle !== data.handle);
      list.push(data);
      saveGarageList(list);
      if (activate) setActiveVehicle(data);
      else refreshVehicleUI();
    }
    function removeFromGarage(handle) {
      const list = loadGarage().filter((c) => c.handle !== handle);
      saveGarageList(list);
      const active = loadVehicle();
      if (active && active.handle === handle) {
        setActiveVehicle(list.length ? list[list.length - 1] : null);
      } else {
        refreshVehicleUI();
      }
    }
    // make, model, trim ('' for single-trim models), handle
    function saveVehicle(make, model, trim, handle) {
      const data = { make, model, trim, handle };
      const list = loadGarage().filter((c) => c.handle !== handle);
      list.push(data);
      saveGarageList(list);
      localStorage.setItem(HOF_KEY, JSON.stringify(data));
      mirrorToGarageKey(data);
      broadcastGarageUpdate();

      // If the drawer's "Shop by System" dispatched an intent before sending
      // the user here to pick a vehicle, redirect to that system's tab inside
      // their vehicle's collection page.
      let pendingSystem = null;
      try { pendingSystem = sessionStorage.getItem('hof_pending_system'); } catch (e) {}
      if (pendingSystem && handle) {
        try { sessionStorage.removeItem('hof_pending_system'); } catch (e) {}
        window.location.href = `${STORE_URL}/collections/${handle}#system=${encodeURIComponent(pendingSystem)}`;
        return; // navigating away — skip the UI re-render
      }

      refreshVehicleUI();

      // Picker was opened from the nav menu's "Select a car" card — close
      // this panel so the user lands back on the still-open menu. The menu's
      // vehicle card has already re-synced via the hof:garage-updated
      // broadcast above.
      if (window.__hofReturnToMenu) {
        window.__hofReturnToMenu = false;
        closePanel();
        if (window.__hofSyncSystems) window.__hofSyncSystems();
      }
    }
    function clearVehicle() {
      setActiveVehicle(null);
    }
    function updateRailVehicle() {
      const saved = loadVehicle();
      const dot = rail.querySelector('#hof-rail-garage-dot');
      if (dot) dot.style.display = saved ? 'block' : 'none';
      const garageIcon = rail.querySelector('#hof-rail-garage');
      if (garageIcon) garageIcon.title = saved ? `Garage: ${vehicleLabel(saved)}` : 'My Garage';
    }

    const messagesEl = panel.querySelector('#hof-messages');
    const inputEl    = panel.querySelector('#hof-input');
    const sendBtn    = panel.querySelector('#hof-send');

    // ── Welcome screen ────────────────────────────────────────────────────────
    function renderWelcome() {
      messagesEl.innerHTML = `
        <div class="hof-welcome">
          <div class="hof-welcome-icon">${ICON_HF}</div>
          <div class="hof-welcome-title">Performance Advisor</div>
          <div class="hof-welcome-sub">Ask about upgrades, parts, or build advice for your European performance car.</div>
          <div class="hof-suggestions">
            ${SUGGESTIONS_LIST.map(s => `<button class="hof-suggestion" data-q="${s}">${s} <span>→</span></button>`).join('')}
          </div>
          <a class="hof-appstore-cta" href="${APP_STORE_URL}" target="_blank" rel="noopener">
            ${ICON_APPLE.replace('currentColor', SLATE_TEXT)}
            <span>Check out our app on the App&nbsp;Store</span>
            <span class="hof-appstore-arrow">→</span>
          </a>
        </div>`;
      messagesEl.querySelectorAll('.hof-suggestion').forEach((el) => {
        el.addEventListener('click', () => send(el.getAttribute('data-q')));
      });
    }
    renderWelcome();

    // ── Shared selector wiring (used by both Garage panel and Search section) ──
    function wireVehicleSelector(container, onSave) {
      const makeEl  = container.querySelector('[data-hof-sel="make"]');
      const modelEl = container.querySelector('[data-hof-sel="model"]');
      const trimRow = container.querySelector('[data-hof-row="trim"]');
      const trimEl  = container.querySelector('[data-hof-sel="trim"]');
      const saveBtn = container.querySelector('[data-hof-btn="save"]');
      const browseBtn = container.querySelector('[data-hof-btn="browse"]');

      function getHandle() {
        if (trimEl && !trimEl.disabled && trimEl.value) return trimEl.value;
        if (modelEl.value) {
          // single-trim: value IS the handle
          const trims = VEHICLES[makeEl.value]?.[modelEl.value];
          if (trims && trims.length === 1) return trims[0].h;
        }
        return '';
      }
      function getTrim() {
        if (trimEl && !trimEl.disabled && trimEl.value) {
          return trimEl.options[trimEl.selectedIndex]?.text || '';
        }
        return ''; // single-trim — no trim label shown
      }
      function setReady(yes) {
        if (saveBtn)   saveBtn.disabled   = !yes;
        if (browseBtn) browseBtn.disabled = !yes;
      }

      makeEl.addEventListener('change', () => {
        const make = makeEl.value;
        modelEl.innerHTML = '<option value="">Select Model</option>';
        modelEl.disabled  = !make;
        if (trimRow) trimRow.style.display = 'none';
        if (trimEl)  { trimEl.innerHTML = '<option value="">Select Trim</option>'; trimEl.disabled = true; }
        setReady(false);
        if (make && VEHICLES[make]) {
          Object.keys(VEHICLES[make]).forEach(model => {
            const opt = document.createElement('option');
            opt.value = model; opt.textContent = model;
            modelEl.appendChild(opt);
          });
        }
      });

      modelEl.addEventListener('change', () => {
        const make  = makeEl.value;
        const model = modelEl.value;
        if (trimRow) trimRow.style.display = 'none';
        if (trimEl)  { trimEl.innerHTML = '<option value="">Select Trim</option>'; trimEl.disabled = true; }
        setReady(false);
        if (!model) return;
        const trims = VEHICLES[make]?.[model];
        if (!trims) return;
        if (trims.length === 1) {
          // Single trim — no dropdown, immediately ready
          setReady(true);
        } else {
          // Multiple trims — show Trim dropdown
          if (trimRow) trimRow.style.display = '';
          if (trimEl) {
            trimEl.disabled = false;
            trims.forEach(({ v, h }) => {
              const opt = document.createElement('option');
              opt.value = h; opt.textContent = v;
              trimEl.appendChild(opt);
            });
          }
        }
      });

      if (trimEl) {
        trimEl.addEventListener('change', () => setReady(!!trimEl.value));
      }

      if (saveBtn) {
        saveBtn.addEventListener('click', () => {
          const make   = makeEl.value;
          const model  = modelEl.value;
          const trim   = getTrim();
          const handle = getHandle();
          if (make && model && handle) onSave(make, model, trim, handle);
        });
      }
      if (browseBtn) {
        browseBtn.addEventListener('click', () => {
          const handle = getHandle();
          if (handle) window.location.href = `${STORE_URL}/collections/${handle}`;
        });
      }
    }

    // ── Garage tab renderer ───────────────────────────────────────────────────
    function renderGarageTab() {
      const garageEl = panel.querySelector('#hof-tab-garage');
      if (!garageEl) return;
      const saved  = loadVehicle();
      const garage = loadGarage();

      const arrowSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M12 5l7 7-7 7" stroke="${MID}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      const quickLinks = `
        <div class="hof-garage-links" style="margin-top:16px">
          ${saved ? `
          <a class="hof-garage-link" href="${STORE_URL}/collections/${saved.handle}" target="_self">
            <div class="hof-garage-link-left">
              <div class="hof-garage-link-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 17H3a2 2 0 01-2-2v-4l2.5-6h13L19 11v4a2 2 0 01-2 2h-2M5 17a2 2 0 104 0 2 2 0 00-4 0zm10 0a2 2 0 104 0 2 2 0 00-4 0z" stroke="${DARK}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </div>
              Parts for My Car
            </div>
            ${arrowSvg}
          </a>` : ''}
          <a class="hof-garage-link" href="${STORE_URL}/collections" target="_self">
            <div class="hof-garage-link-left">
              <div class="hof-garage-link-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M3 3h18M3 9h18M3 15h18M3 21h18" stroke="${DARK}" stroke-width="2" stroke-linecap="round"/></svg>
              </div>
              Browse All Parts
            </div>
            ${arrowSvg}
          </a>
          <a class="hof-garage-link" href="${STORE_URL}/pages/contact" target="_self">
            <div class="hof-garage-link-left">
              <div class="hof-garage-link-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 3C7.03 3 3 6.58 3 11c0 2.11.87 4.03 2.29 5.47L4 21l4.69-1.23C9.97 20.57 10.97 21 12 21c4.97 0 9-3.58 9-8s-4.03-8-9-8z" stroke="${DARK}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </div>
              Talk to a Specialist
            </div>
            ${arrowSvg}
          </a>
          <a class="hof-garage-link" href="${STORE_URL}/account" target="_self">
            <div class="hof-garage-link-left">
              <div class="hof-garage-link-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" stroke="${DARK}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </div>
              My Account
            </div>
            ${arrowSvg}
          </a>
          <a class="hof-garage-link" href="${APP_STORE_URL}" target="_blank" rel="noopener">
            <div class="hof-garage-link-left">
              <div class="hof-garage-link-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="${DARK}"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
              </div>
              Get the iOS App
            </div>
            ${arrowSvg}
          </a>
        </div>`;

      if (garage.length) {
        const makes = Object.keys(VEHICLES);
        garageEl.innerHTML = `
          <div class="hof-vs-label" style="margin-bottom:8px">My Garage · tap a car to make it active</div>
          <div class="hof-garage-cars">
            ${garage.map((c, i) => {
              const isActive = saved && saved.handle === c.handle;
              return `
              <div class="hof-garage-car${isActive ? ' active' : ''}" data-hof-car="${i}" role="button" tabindex="0">
                <div class="hof-garage-car-dot"></div>
                <div class="hof-garage-car-info">
                  <div class="hof-garage-car-make">${c.make}${isActive ? ' · Active' : ''}</div>
                  <div class="hof-garage-car-name">${vehicleLabel(c)}</div>
                </div>
                <button class="hof-garage-car-remove" data-hof-remove="${i}" aria-label="Remove from garage" title="Remove">×</button>
              </div>`;
            }).join('')}
          </div>
          <button class="hof-garage-add-btn" id="hof-garage-add">+ Add another car</button>
          <div class="hof-garage-add-form" id="hof-garage-add-form" style="display:none">
            <div class="hof-vs-row">
              <label class="hof-vs-label">Make</label>
              <select class="hof-select" data-hof-sel="make">
                <option value="">Choose make…</option>
                ${makes.map(m => `<option value="${m}">${m}</option>`).join('')}
              </select>
            </div>
            <div class="hof-vs-row">
              <label class="hof-vs-label">Model</label>
              <select class="hof-select" data-hof-sel="model" disabled>
                <option value="">Select Model</option>
              </select>
            </div>
            <div class="hof-vs-row" data-hof-row="trim" style="display:none">
              <label class="hof-vs-label">Trim</label>
              <select class="hof-select" data-hof-sel="trim" disabled>
                <option value="">Select Trim</option>
              </select>
            </div>
            <button class="hof-vs-btn" data-hof-btn="save" disabled>Save to Garage</button>
          </div>
          ${quickLinks}`;

        garageEl.querySelectorAll('[data-hof-car]').forEach((el) => {
          el.addEventListener('click', (e) => {
            if (e.target.closest('[data-hof-remove]')) return;
            const car = garage[+el.getAttribute('data-hof-car')];
            if (car) setActiveVehicle(car);
          });
          el.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              const car = garage[+el.getAttribute('data-hof-car')];
              if (car) setActiveVehicle(car);
            }
          });
        });
        garageEl.querySelectorAll('[data-hof-remove]').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const car = garage[+btn.getAttribute('data-hof-remove')];
            if (car) removeFromGarage(car.handle);
          });
        });
        const addBtn  = garageEl.querySelector('#hof-garage-add');
        const addForm = garageEl.querySelector('#hof-garage-add-form');
        addBtn.addEventListener('click', () => {
          const show = addForm.style.display === 'none';
          addForm.style.display = show ? '' : 'none';
          addBtn.textContent = show ? 'Cancel' : '+ Add another car';
        });
        wireVehicleSelector(addForm, saveVehicle);
      } else {
        const makes = Object.keys(VEHICLES);
        garageEl.innerHTML = `
          <div class="hof-garage-hero" style="margin-bottom:20px">
            <div class="hof-garage-hero-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M5 17H3a2 2 0 01-2-2v-4l2.5-6h13L19 11v4a2 2 0 01-2 2h-2M5 17a2 2 0 104 0 2 2 0 00-4 0zm10 0a2 2 0 104 0 2 2 0 00-4 0z" stroke="${LIGHT}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </div>
            <div class="hof-garage-hero-title">Select Your Vehicle</div>
            <div class="hof-garage-hero-sub">Get fitment-verified parts and personalised recommendations.</div>
            ${APP_LIVE ? `
            <div class="hof-app-btns">
              <a class="hof-app-btn" href="https://apps.apple.com/us/app/house-of-fuel/id6761641912" target="_blank" rel="noopener">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="${LIGHT}"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                Download on the App Store
              </a>
            </div>` : ''}
          </div>
          <div class="hof-vs-row">
            <label class="hof-vs-label">Make</label>
            <select class="hof-select" data-hof-sel="make">
              <option value="">Choose make…</option>
              ${makes.map(m => `<option value="${m}">${m}</option>`).join('')}
            </select>
          </div>
          <div class="hof-vs-row">
            <label class="hof-vs-label">Model</label>
            <select class="hof-select" data-hof-sel="model" disabled>
              <option value="">Select Model</option>
            </select>
          </div>
          <div class="hof-vs-row" data-hof-row="trim" style="display:none">
            <label class="hof-vs-label">Trim</label>
            <select class="hof-select" data-hof-sel="trim" disabled>
              <option value="">Select Trim</option>
            </select>
          </div>
          <button class="hof-vs-btn" data-hof-btn="save" disabled>Save to Garage</button>
          ${quickLinks}`;
        wireVehicleSelector(garageEl, saveVehicle);
      }
    }

    // ── Advisor active-vehicle chip ───────────────────────────────────────────
    function renderAdvisorChip() {
      const bar = panel.querySelector('#hof-advisor-vehicle');
      if (!bar) return;
      const saved  = loadVehicle();
      const garage = loadGarage();
      const carSvg = `<svg viewBox="0 0 24 24" fill="none"><path d="M5 17H3a2 2 0 01-2-2v-4l2.5-6h13L19 11v4a2 2 0 01-2 2h-2M5 17a2 2 0 104 0 2 2 0 00-4 0zm10 0a2 2 0 104 0 2 2 0 00-4 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

      if (garage.length === 0) {
        bar.innerHTML = `
          <button class="hof-veh-chip" id="hof-veh-chip">
            ${carSvg}
            <span class="hof-veh-chip-label">Set your car for exact-fit advice</span>
          </button>`;
        bar.querySelector('#hof-veh-chip').addEventListener('click', () => switchTab('garage'));
        return;
      }

      const label = saved ? `${saved.make} ${vehicleLabel(saved)}` : 'Choose your car';
      bar.innerHTML = `
        <button class="hof-veh-chip" id="hof-veh-chip" aria-haspopup="true">
          ${carSvg}
          <span class="hof-veh-chip-label">${label}</span>
          <span class="hof-veh-chip-caret">▼</span>
        </button>
        <div class="hof-veh-menu" id="hof-veh-menu">
          ${garage.map((c, i) => `
            <button class="hof-veh-menu-item" data-hof-pick="${i}">
              ${c.make} ${vehicleLabel(c)}
              ${saved && saved.handle === c.handle ? '<span class="check">✓</span>' : ''}
            </button>`).join('')}
          <button class="hof-veh-menu-item add" data-hof-pick="add">+ Add a car</button>
        </div>`;

      const chip = bar.querySelector('#hof-veh-chip');
      const menu = bar.querySelector('#hof-veh-menu');
      chip.addEventListener('click', (e) => {
        e.stopPropagation();
        menu.classList.toggle('open');
      });
      bar.querySelectorAll('[data-hof-pick]').forEach((item) => {
        item.addEventListener('click', () => {
          menu.classList.remove('open');
          const pick = item.getAttribute('data-hof-pick');
          if (pick === 'add') { switchTab('garage'); return; }
          const car = garage[+pick];
          if (car) setActiveVehicle(car);
        });
      });
    }
    renderAdvisorChip();
    document.addEventListener('click', () => {
      const menu = panel.querySelector('#hof-veh-menu');
      if (menu) menu.classList.remove('open');
    });

    // ── "Add to garage" suggestion after a reply about an unsaved car ─────────
    function offerAddToGarage(vehicle, bubble) {
      if (!vehicle || !vehicle.handle || !bubble) return;
      // Only for cars the advisor detected/switched to from the message text
      if (vehicle.source !== 'mentioned' && vehicle.source !== 'detected') return;
      if (loadGarage().some((c) => c.handle === vehicle.handle)) return;
      const entry = vehicleFromHandle(vehicle.handle);
      if (!entry) return; // brand-level or unknown handle — nothing to save
      const chip = document.createElement('button');
      chip.className = 'hof-add-garage-chip';
      chip.textContent = `+ Add ${vehicle.name || vehicleLabel(entry)} to my garage`;
      chip.addEventListener('click', () => {
        addToGarage(entry, true);
        chip.textContent = '✓ Added to your garage';
        chip.disabled = true;
        chip.classList.add('added');
      });
      bubble.appendChild(document.createElement('br'));
      bubble.appendChild(chip);
      scrollBottom();
    }

    // ── Format assistant message ──────────────────────────────────────────────
    function formatMessage(text) {
      let out = text
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      out = out.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
      out = out.replace(
        /(https?:\/\/[^\s<>"&]+)/g,
        '<a href="$1" target="_blank" rel="noopener noreferrer" class="hof-product-link">View Product →</a>'
      );
      out = out.replace(
        /^(\d+)\.\s+(.+)$/gm,
        '<span class="hof-list-item"><span class="hof-list-num">$1</span><span class="hof-list-text">$2</span></span>'
      );
      out = out.replace(
        /^(Step\s+\d+[.:])(.*)$/gim,
        '<span class="hof-step-label">$1</span>$2'
      );
      out = out.replace(/\n{2,}/g, '<br><br>').replace(/\n/g, '<br>');
      return out;
    }

    // ── Bubbles ───────────────────────────────────────────────────────────────
    function appendBubble(role, content, isError) {
      const isUser = role === 'user';
      const row    = document.createElement('div');
      row.className = `hof-row${isUser ? ' user' : ''}`;
      if (!isUser) {
        row.innerHTML += `<div class="hof-avatar${isError ? ' error' : ''}">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="${LIGHT}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>`;
      }
      const bubble = document.createElement('div');
      bubble.className = `hof-bubble ${isUser ? 'user' : 'assistant'}${isError ? ' error' : ''}`;
      bubble.innerHTML = isUser
        ? content.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        : formatMessage(content);
      row.appendChild(bubble);
      messagesEl.appendChild(row);
      scrollBottom();
      return bubble;
    }

    // ── Typing indicator ──────────────────────────────────────────────────────
    let typingEl = null;
    function showTyping() {
      if (typingEl) return;
      const row = document.createElement('div');
      row.className = 'hof-row';
      row.innerHTML = `
        <div class="hof-avatar"><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="${LIGHT}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div>
        <div class="hof-bubble assistant hof-typing"><div class="hof-dot"></div><div class="hof-dot"></div><div class="hof-dot"></div></div>`;
      messagesEl.appendChild(row);
      typingEl = row;
      scrollBottom();
    }
    function hideTyping() { if (typingEl) { typingEl.remove(); typingEl = null; } }
    function scrollBottom() { setTimeout(() => { messagesEl.scrollTop = messagesEl.scrollHeight; }, 40); }

    // ── Send ──────────────────────────────────────────────────────────────────
    async function send(text) {
      const content = (text || inputEl.value).trim();
      if (!content || isLoading) return;
      if (chatHistory.length === 0) messagesEl.innerHTML = '';
      inputEl.value = '';
      inputEl.style.height = 'auto';
      sendBtn.disabled = true;
      chatHistory.push({ role: 'user', content });
      appendBubble('user', content);
      isLoading = true;
      showTyping();
      try {
        // Pass the saved garage vehicle + its collection handle (same shape the
        // app sends) so the advisor gives fitment-correct, in-stock product
        // recommendations instead of generic answers. Page context is appended
        // when present so it also knows what the customer is looking at.
        const savedV = loadVehicle();
        const ctxParts = [];
        if (savedV) ctxParts.push(`${savedV.make} ${vehicleLabel(savedV)}`);
        if (pageContext) ctxParts.push(pageContext);
        const res = await fetch(PROXY_URL, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({
            messages: chatHistory,
            vehicleContext: ctxParts.length ? ctxParts.join('. ') : undefined,
            vehicleCollection: savedV ? savedV.handle : undefined,
            stream: true,
          }),
        });
        if (!res.ok) {
          hideTyping();
          let msg = `Error ${res.status}`;
          try { const j = await res.json(); if (j.error) msg = j.error; } catch (e) {}
          throw new Error(msg);
        }
        const ct = res.headers.get('content-type') || '';
        // ── Streaming path (Server-Sent Events) — token-by-token like the app ──
        if (ct.includes('text/event-stream') && res.body && res.body.getReader) {
          hideTyping();
          const bubble = appendBubble('assistant', '');
          const reader  = res.body.getReader();
          const decoder = new TextDecoder();
          let buf = '', full = '', replyVehicle = null;
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            const lines = buf.split('\n');
            buf = lines.pop() || '';
            for (const line of lines) {
              const t = line.trim();
              if (!t.startsWith('data:')) continue;
              const payload = t.slice(5).trim();
              if (!payload) continue;
              try {
                const ev = JSON.parse(payload);
                if (ev.type === 'delta' && ev.text) {
                  full += ev.text;
                  bubble.innerHTML = formatMessage(full);
                  scrollBottom();
                } else if (ev.type === 'done' && ev.reply) {
                  full = ev.reply;
                  if (ev.vehicle) replyVehicle = ev.vehicle;
                  bubble.innerHTML = formatMessage(full);
                  scrollBottom();
                }
              } catch (e) { /* keep-alive / partial line */ }
            }
          }
          chatHistory.push({ role: 'assistant', content: full });
          offerAddToGarage(replyVehicle, bubble);
        } else {
          // ── Non-streaming JSON fallback ──
          hideTyping();
          const data = await res.json();
          if (data.error) throw new Error(data.error);
          chatHistory.push({ role: 'assistant', content: data.reply });
          const jsonBubble = appendBubble('assistant', data.reply);
          offerAddToGarage(data.vehicle, jsonBubble);
        }
      } catch (e) {
        hideTyping();
        appendBubble('assistant', `Something went wrong: ${e.message}`, true);
      } finally {
        isLoading = false;
      }
    }

    inputEl.addEventListener('input', function () {
      sendBtn.disabled = !this.value.trim();
      this.style.height = 'auto';
      this.style.height = Math.min(this.scrollHeight, 88) + 'px';
    });
    inputEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
    });
    sendBtn.addEventListener('click', () => send());

    // ── Fitment banner (product pages) ───────────────────────────────────────
    function injectFitmentBanner(saved, collections) {
      const fits = collections.includes(saved.handle);
      const existing = document.getElementById('hof-fitment-banner');
      if (existing) existing.remove();

      const banner = document.createElement('div');
      banner.id = 'hof-fitment-banner';
      banner.className = fits ? 'hof-fits' : 'hof-no-fit';
      banner.innerHTML = `
        <svg class="hof-banner-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
          ${fits
            ? '<path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>'
            : '<path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>'}
        </svg>
        <span class="hof-banner-text">
          ${fits
            ? `Fits your <strong>${vehicleLabel(saved)}</strong>`
            : `May not fit your <strong>${vehicleLabel(saved)}</strong>`}
        </span>
        ${!fits ? `<a href="${STORE_URL}/collections/${saved.handle}" class="hof-banner-cta">Shop my car →</a>` : ''}
        <button class="hof-banner-x" id="hof-banner-x">×</button>`;

      document.body.insertBefore(banner, document.body.firstChild);
      setTimeout(() => banner.classList.add('hof-banner-visible'), 80);
      banner.querySelector('#hof-banner-x').addEventListener('click', () => {
        banner.classList.remove('hof-banner-visible');
        setTimeout(() => banner.remove(), 350);
      });
    }

    function tryFitmentBanner(attempts) {
      if (attempts <= 0) return;
      const saved = loadVehicle();
      if (!saved || !window.location.pathname.includes('/products/')) return;
      const cols = window.easysearch && window.easysearch.pageInfo && window.easysearch.pageInfo.productCollections;
      if (cols && Array.isArray(cols)) {
        injectFitmentBanner(saved, cols);
      } else {
        setTimeout(() => tryFitmentBanner(attempts - 1), 1200);
      }
    }

    // ── Tab switching ─────────────────────────────────────────────────────────
    function switchTab(tab) {
      activeTab = tab;
      panel.querySelectorAll('.hof-nav-tab').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.tab === tab);
      });
      panel.querySelectorAll('.hof-tab-content').forEach(el => {
        el.classList.toggle('active', el.id === `hof-tab-${tab}`);
      });
      if (tab === 'advisor') setTimeout(() => inputEl.focus(), 200);
      if (tab === 'garage') renderGarageTab();
    }

    panel.querySelectorAll('.hof-nav-tab').forEach(btn => {
      btn.addEventListener('click', () => switchTab(btn.dataset.tab));
    });

    // ── Open / close ──────────────────────────────────────────────────────────
    function openPanel(tab) {
      isOpen = true;
      if (tab) switchTab(tab);
      panel.classList.add('hof-open');
      overlay.classList.add('hof-visible');
      document.body.style.overflow = window.innerWidth <= 600 ? 'hidden' : '';
    }

    function closePanel() {
      isOpen = false;
      panel.classList.remove('hof-open');
      overlay.classList.remove('hof-visible');
      document.body.style.overflow = '';
    }

    // Rail icon clicks
    rail.querySelector('#hof-rail-advisor').addEventListener('click', (e) => {
      e.stopPropagation();
      isOpen && activeTab === 'advisor' ? closePanel() : openPanel('advisor');
    });
    rail.querySelector('#hof-rail-garage').addEventListener('click', (e) => {
      e.stopPropagation();
      isOpen && activeTab === 'garage' ? closePanel() : openPanel('garage');
    });
    // Clicking the rail body (chevron area) toggles
    rail.addEventListener('click', () => {
      isOpen ? closePanel() : openPanel(activeTab);
    });

    panel.querySelector('#hof-panel-close').addEventListener('click', closePanel);
    overlay.addEventListener('click', closePanel);

    // Esc key
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && isOpen) closePanel(); });

    // ── Drawer integration ───────────────────────────────────────────────────
    // The side-by-side drawer's Systems tab dispatches `hof:open-vehicle-picker`
    // when a user clicks a system row without a vehicle saved. We respond by
    // opening this widget's garage tab; `saveVehicle` then reads
    // `hof_pending_system` from sessionStorage and routes to the correct
    // collection + system tab.
    function onPickVehicle(e) {
      // "select-vehicle" = opened from the menu's "Select a car" card; the
      // menu stays open underneath, so flag saveVehicle to close this panel
      // and return the user to it. System-row intents navigate away on save
      // instead, so no flag for those.
      window.__hofReturnToMenu = !!(e && e.detail && e.detail.intent === 'select-vehicle');
      openPanel('garage');
    }
    window.addEventListener('hof:open-vehicle-picker', onPickVehicle);
    document.addEventListener('hof:open-vehicle-picker', onPickVehicle);

    // ── Search-by-vehicle section (replaces EasySearch bar) ──────────────────
    function renderSearchSection() {
      const el = document.getElementById('hof-search-section');
      if (!el) return;
      const saved  = loadVehicle();
      const garage = loadGarage();
      const makes  = Object.keys(VEHICLES);

      const formHTML = `
          <div class="hof-ss-row">
            <div class="hof-ss-field">
              <span class="hof-ss-label">Make</span>
              <select data-hof-sel="make">
                <option value="">Select Make</option>
                ${makes.map(m => `<option value="${m}">${m}</option>`).join('')}
              </select>
            </div>
            <div class="hof-ss-field">
              <span class="hof-ss-label">Model</span>
              <select data-hof-sel="model" disabled>
                <option value="">Select Model</option>
              </select>
            </div>
            <div class="hof-ss-field" data-hof-row="trim" style="display:none">
              <span class="hof-ss-label">Trim</span>
              <select data-hof-sel="trim" disabled>
                <option value="">Select Trim</option>
              </select>
            </div>
            <div class="hof-ss-actions">
              <button class="hof-ss-btn-primary" data-hof-btn="browse" disabled>Search</button>
              <button class="hof-ss-btn-secondary" data-hof-btn="save" disabled>+ Add to Garage</button>
            </div>
          </div>`;

      if (garage.length) {
        // ── Garage strip: card per saved car; design/CSS lives in the
        //    HOF-Garage.liquid theme section (auto light/dark via --hof-g-*) ──
        el.innerHTML = `
          <div class="hof-ss-heading">Shop by Vehicle</div>
          <div class="hof-ss-garage">
            ${garage.map((c, i) => {
              const act = saved && saved.handle === c.handle;
              return `
              <div class="hof-ss-car${act ? ' active' : ''}" data-hof-ss-car="${i}" role="button" tabindex="0" title="${act ? 'Active vehicle' : 'Set as active vehicle'}">
                <span class="hof-ss-car-make">${c.make}</span>
                <span class="hof-ss-car-name">${vehicleLabel(c)}</span>
                <span class="hof-ss-car-foot">
                  ${act
                    ? `<span class="hof-ss-car-badge">Active</span><a class="hof-ss-car-browse" href="${STORE_URL}/collections/${c.handle}">Browse parts →</a>`
                    : `<span class="hof-ss-car-set">Set active</span>`}
                </span>
              </div>`;
            }).join('')}
            <div class="hof-ss-car hof-ss-car-add" id="hof-ss-add" role="button" tabindex="0">+ Add a car</div>
          </div>
          <div id="hof-ss-form" class="hof-ss-form" style="display:none">${formHTML}</div>`;

        el.querySelectorAll('[data-hof-ss-car]').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            if (e.target.closest('a')) return; // Browse parts link navigates
            const car = garage[+btn.getAttribute('data-hof-ss-car')];
            if (car) setActiveVehicle(car); // re-renders this section + widget + drawer
          });
        });
        const addBtn = el.querySelector('#hof-ss-add');
        const form   = el.querySelector('#hof-ss-form');
        addBtn.addEventListener('click', () => {
          const show = form.style.display === 'none';
          form.style.display = show ? '' : 'none';
          addBtn.textContent = show ? '× Cancel' : '+ Add a car';
        });
        wireVehicleSelector(form, saveVehicle);
      } else {
        el.innerHTML = `
          <div class="hof-ss-heading">Search by Vehicle</div>
          <div class="hof-ss-row">
            <div class="hof-ss-field">
              <span class="hof-ss-label">Make</span>
              <select data-hof-sel="make">
                <option value="">Select Make</option>
                ${makes.map(m => `<option value="${m}">${m}</option>`).join('')}
              </select>
            </div>
            <div class="hof-ss-field">
              <span class="hof-ss-label">Model</span>
              <select data-hof-sel="model" disabled>
                <option value="">Select Model</option>
              </select>
            </div>
            <div class="hof-ss-field" data-hof-row="trim" style="display:none">
              <span class="hof-ss-label">Trim</span>
              <select data-hof-sel="trim" disabled>
                <option value="">Select Trim</option>
              </select>
            </div>
            <div class="hof-ss-actions">
              <button class="hof-ss-btn-primary" data-hof-btn="browse" disabled>Search</button>
              <button class="hof-ss-btn-secondary" data-hof-btn="save" disabled>+ Add to Garage</button>
            </div>
          </div>`;
        wireVehicleSelector(el, saveVehicle);
      }
    }

    // ── Product page fitment block ────────────────────────────────────────────
    // Hydrates the server-rendered #hof-fitment-block (from the
    // `vehicle_fitment` theme block). No-op if the block isn't on the page —
    // e.g. inquiry-to-order templates (wheels) simply omit it.
    // Re-runs on save/clear so state stays in sync without a reload.
    function hydrateProductFitmentBlock() {
      const block = document.getElementById('hof-fitment-block');
      if (!block) return;

      const storeUrl      = block.dataset.storeUrl || STORE_URL;
      const labelFits     = block.dataset.labelFits      || '✓ Fits this product';
      const labelNoFit    = block.dataset.labelNoFit     || '✗ Not verified for your vehicle';
      const labelShop     = block.dataset.labelShopCta   || 'Shop my car';
      const emptyVehicle  = block.dataset.labelEmptyVehicle || 'No vehicle selected';
      const emptyStatus   = block.dataset.labelEmptyStatus  || 'Select your vehicle to check fitment';
      const selectCta     = block.dataset.labelSelectCta || 'Select Vehicle';
      const collections   = (block.dataset.collections || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

      const saved = loadVehicle();
      const body  = block.querySelector('[data-hof-fb-body]');
      if (!body) return;

      if (saved) {
        const fits = collections.includes(saved.handle);
        const iconColor = fits ? '#16a34a' : '#dc2626';
        const iconPath  = fits
          ? '<path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>'
          : '<path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>';
        body.innerHTML = `
          <div class="hof-fb-icon ${fits ? 'fits' : 'no-fit'}">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style="color:${iconColor}">${iconPath}</svg>
          </div>
          <div class="hof-fb-info">
            <div class="hof-fb-vehicle">${saved.make} ${vehicleLabel(saved)}</div>
            <div class="hof-fb-status ${fits ? 'fits' : 'no-fit'}">${fits ? labelFits : labelNoFit}</div>
          </div>
          ${!fits ? `<a href="${storeUrl}/collections/${saved.handle}" class="hof-fb-action">${labelShop}</a>` : ''}`;
      } else {
        // Render (or re-render) empty state. Wires up the "Select Vehicle" button.
        body.innerHTML = `
          <div class="hof-fb-icon empty">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 17H3a2 2 0 01-2-2v-4l2.5-6h13L19 11v4a2 2 0 01-2 2h-2M5 17a2 2 0 104 0 2 2 0 00-4 0zm10 0a2 2 0 104 0 2 2 0 00-4 0z" stroke="${MID}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="hof-fb-info">
            <div class="hof-fb-vehicle" style="color:${MID};font-weight:500">${emptyVehicle}</div>
            <div class="hof-fb-status empty">${emptyStatus}</div>
          </div>
          <button type="button" class="hof-fb-action" data-hof-fb-open-garage>${selectCta}</button>`;
        body.querySelector('[data-hof-fb-open-garage]')
          ?.addEventListener('click', () => openPanel('garage'));
      }
    }

    // ── Init garage + fitment ─────────────────────────────────────────────────
    // Sync hof_garage from current hof_vehicle so the drawer's Systems pane
    // hydrates on page load without waiting for a save.
    mirrorToGarageKey(loadVehicle());

    renderGarageTab();
    updateRailVehicle();
    renderSearchSection();
    hydrateProductFitmentBlock();
  }

  // ── Init ──────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', buildWidget);
  } else {
    buildWidget();
  }

  // ── Rail: Draggable + Collapsible enhancement ─────────────────────────────
  // Self-contained. Finds #hof-rail after buildWidget mounts it, then wires
  // drag + collapse without touching the existing rail/panel code.
  // localStorage keys: hof_rail_pos, hof_rail_collapsed
  (function enhanceHofRail() {
    const rail = document.getElementById('hof-rail');
    if (!rail) {
      // Rail not mounted yet — retry shortly.
      return void setTimeout(enhanceHofRail, 50);
    }

    const POS_KEY = 'hof_rail_pos';
    const COLLAPSED_KEY = 'hof_rail_collapsed';
    const DRAG_THRESHOLD = 5; // px — below this, treat pointerup as a click
    const MOBILE_MAX = 600;   // px — disable drag at or below this viewport width

    // ---------- Inject CSS ----------------------------------------------------
    const style = document.createElement('style');
    style.textContent = `
      @media (min-width: ${MOBILE_MAX + 1}px) {
        #hof-rail { cursor: grab; touch-action: none; user-select: none; }
        #hof-rail.hof-rail-dragging { cursor: grabbing !important; transition: none !important; }
      }
      #hof-rail-collapse {
        background: transparent;
        border: 0;
        color: rgba(241,241,239,0.7);
        font: 600 16px/1 system-ui, -apple-system, sans-serif;
        padding: 4px 6px;
        margin-bottom: 4px;
        cursor: pointer;
        border-radius: 6px;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      #hof-rail-collapse:hover { background: rgba(255,255,255,0.10); color: #fff; }
      #hof-rail.hof-rail-collapsed { padding: 6px 4px; gap: 0; }
      #hof-rail.hof-rail-collapsed .hof-rail-icon,
      #hof-rail.hof-rail-collapsed .hof-rail-divider { display: none; }
      #hof-rail.hof-rail-collapsed #hof-rail-collapse { margin: 0; padding: 8px 4px; }
      #hof-rail.hof-rail-edge-left {
        border-radius: 0 12px 12px 0 !important;
        box-shadow: 4px 0 24px rgba(0,0,0,0.25) !important;
      }
    `;
    document.head.appendChild(style);

    // ---------- Collapse button ----------------------------------------------
    const collapseBtn = document.createElement('button');
    collapseBtn.id = 'hof-rail-collapse';
    collapseBtn.type = 'button';
    collapseBtn.setAttribute('aria-label', 'Collapse toolkit');
    collapseBtn.textContent = '×';
    rail.insertBefore(collapseBtn, rail.firstChild);

    let isCollapsed = localStorage.getItem(COLLAPSED_KEY) === '1';
    applyCollapsed();

    function applyCollapsed() {
      rail.classList.toggle('hof-rail-collapsed', isCollapsed);
      collapseBtn.textContent = isCollapsed ? '‹' : '×';
      collapseBtn.setAttribute('aria-label', isCollapsed ? 'Expand toolkit' : 'Collapse toolkit');
    }

    function setCollapsed(value) {
      isCollapsed = !!value;
      try { localStorage.setItem(COLLAPSED_KEY, isCollapsed ? '1' : '0'); } catch (e) {}
      applyCollapsed();
    }

    collapseBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      setCollapsed(!isCollapsed);
    });

    // When collapsed, any click anywhere on the rail expands it (and we swallow
    // the event so the existing advisor/garage handlers don't also fire).
    rail.addEventListener('click', (e) => {
      if (isCollapsed && !e.target.closest('#hof-rail-collapse')) {
        e.stopPropagation();
        e.stopImmediatePropagation();
        setCollapsed(false);
      }
    }, true); // capture phase — runs before existing bubble handlers

    // ---------- Position restore ---------------------------------------------
    function applyPosition(pos) {
      rail.style.top = pos.top + 'px';
      rail.style.transform = 'none';
      if (pos.edge === 'left') {
        rail.style.left = '0';
        rail.style.right = 'auto';
        rail.classList.add('hof-rail-edge-left');
      } else {
        rail.style.right = '0';
        rail.style.left = 'auto';
        rail.classList.remove('hof-rail-edge-left');
      }
    }

    if (window.innerWidth > MOBILE_MAX) {
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem(POS_KEY) || 'null'); } catch (e) {}
      if (saved && typeof saved.top === 'number' && (saved.edge === 'left' || saved.edge === 'right')) {
        // Clamp so a position saved on a taller window still fits.
        const maxTop = Math.max(0, window.innerHeight - rail.offsetHeight - 8);
        const clampedTop = Math.max(8, Math.min(maxTop, saved.top));
        applyPosition({ edge: saved.edge, top: clampedTop });
      }
    }

    // ---------- Drag logic ---------------------------------------------------
    let drag = null;

    rail.addEventListener('pointerdown', (e) => {
      if (window.innerWidth <= MOBILE_MAX) return;          // no drag on mobile
      if (isCollapsed) return;                               // collapsed tab just re-expands
      if (e.target.closest('#hof-rail-collapse')) return;    // let the button click
      if (e.button !== undefined && e.button !== 0) return;  // left mouse only

      const rect = rail.getBoundingClientRect();
      drag = {
        startX: e.clientX,
        startY: e.clientY,
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
        pointerId: e.pointerId,
        dragging: false,
      };
    });

    rail.addEventListener('pointermove', (e) => {
      if (!drag || e.pointerId !== drag.pointerId) return;

      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;

      if (!drag.dragging) {
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
        // Threshold crossed — engage drag. Capture AFTER threshold so that a
        // simple click keeps firing on its intended child (advisor/garage icon).
        drag.dragging = true;
        try { rail.setPointerCapture(drag.pointerId); } catch (err) {}
        rail.classList.add('hof-rail-dragging');
        rail.classList.remove('hof-rail-edge-left');
      }

      const maxLeft = window.innerWidth - rail.offsetWidth;
      const maxTop = window.innerHeight - rail.offsetHeight;
      const newLeft = Math.max(0, Math.min(maxLeft, e.clientX - drag.offsetX));
      const newTop = Math.max(0, Math.min(maxTop, e.clientY - drag.offsetY));

      rail.style.left = newLeft + 'px';
      rail.style.top = newTop + 'px';
      rail.style.right = 'auto';
      rail.style.transform = 'none';
    });

    function endDrag(e) {
      if (!drag) return;
      const wasDragging = drag.dragging;
      try { rail.releasePointerCapture(drag.pointerId); } catch (err) {}
      rail.classList.remove('hof-rail-dragging');
      drag = null;

      if (!wasDragging) return;

      // Snap to nearest vertical edge.
      const rect = rail.getBoundingClientRect();
      const edge = (rect.left + rect.width / 2) < (window.innerWidth / 2) ? 'left' : 'right';
      const top = rect.top;
      applyPosition({ edge: edge, top: top });
      try { localStorage.setItem(POS_KEY, JSON.stringify({ edge: edge, top: top })); } catch (err) {}

      // Suppress the click that fires right after pointerup (otherwise the
      // end-of-drag would also toggle the panel open/closed).
      const suppress = (ev) => {
        ev.stopPropagation();
        ev.stopImmediatePropagation();
        ev.preventDefault();
      };
      rail.addEventListener('click', suppress, { capture: true, once: true });
      setTimeout(() => rail.removeEventListener('click', suppress, true), 150);
    }

    rail.addEventListener('pointerup', endDrag);
    rail.addEventListener('pointercancel', endDrag);

    // Re-clamp if the viewport shrinks (rotate, window resize).
    window.addEventListener('resize', () => {
      if (window.innerWidth <= MOBILE_MAX) return;
      const rect = rail.getBoundingClientRect();
      const maxTop = Math.max(0, window.innerHeight - rail.offsetHeight - 8);
      if (rect.top > maxTop) {
        const edge = (rect.left + rect.width / 2) < (window.innerWidth / 2) ? 'left' : 'right';
        applyPosition({ edge: edge, top: maxTop });
        try { localStorage.setItem(POS_KEY, JSON.stringify({ edge: edge, top: maxTop })); } catch (err) {}
      }
    });
  })();
})();